import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmt = (n) =>
  Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Ürün avatar ──────────────────────────────────────────────────────────────
function ProductAvatar({ name, imageUrl, size = 48 }) {
  const [err, setErr] = useState(false);
  const initials = (name || "?").split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const palettes = [
    ["#e0f2fe","#0284c7"],["#dcfce7","#16a34a"],["#fef3c7","#d97706"],
    ["#f3e8ff","#7c3aed"],["#ffe4e6","#e11d48"],["#e0f2f1","#0d9488"],
  ];
  const [bg, fg] = palettes[initials.charCodeAt(0) % palettes.length];
  if (imageUrl && !err)
    return <img src={imageUrl} alt={name} onError={() => setErr(true)}
      className="rounded-xl object-cover flex-shrink-0" style={{ width: size, height: size }} />;
  return (
    <div className="rounded-xl flex items-center justify-center font-bold flex-shrink-0"
      style={{ width: size, height: size, background: bg, color: fg, fontSize: size * 0.32 }}>
      {initials}
    </div>
  );
}

function StockPill({ stock }) {
  if (stock == null) return null;
  if (stock <= 0) return <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600">Tükendi</span>;
  if (stock < 5)  return <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Son {stock}</span>;
  return <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{stock}</span>;
}

// ── Icon yardımcıları ────────────────────────────────────────────────────────
const IconX = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

// ════════════════════════════════════════════════════════════════════════════
export default function POSPage() {
  const navigate = useNavigate();

  const [products, setProducts]           = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [search, setSearch]               = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  // Sepet
  const [cart, setCart]             = useState([]);
  const [globalDiscount, setGlobalDiscount] = useState("");
  const [discountMode, setDiscountMode] = useState("percent"); // "percent" | "amount"
  const [activeSession, setActiveSession] = useState(1);
  const [sessions, setSessions]     = useState({ 1:[], 2:[], 3:[], 4:[] });

  // Müşteri
  const [customer, setCustomer]     = useState(null); // { name, email, phone, id? }
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customers, setCustomers]   = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);

  // Modaller
  const [showPriceModal, setShowPriceModal]       = useState(false);
  const [priceQuery, setPriceQuery]               = useState("");
  const [foundProduct, setFoundProduct]           = useState(null);

  const [showPaymentModal, setShowPaymentModal]   = useState(false);
  const [paymentType, setPaymentType]             = useState("nakit");
  const [paymentAmount, setPaymentAmount]         = useState("");

  const [showSplitModal, setShowSplitModal]       = useState(false);
  const [splitNakit, setSplitNakit]               = useState("");
  const [splitKart, setSplitKart]                 = useState("");

  const [completingSale, setCompletingSale]       = useState(false);
  const [showReceiptModal, setShowReceiptModal]   = useState(false);
  const [lastSale, setLastSale]                   = useState(null);

  // Tam ekran
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Refs
  const searchRef     = useRef(null);
  const priceInputRef = useRef(null);
  const payAmountRef  = useRef(null);
  const custSearchRef = useRef(null);

  // ── Tam ekran ──────────────────────────────────────────────────────────
  useEffect(() => {
    const onChange = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      document.documentElement.classList.toggle("pos-fullscreen", fs);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.documentElement.classList.remove("pos-fullscreen");
    };
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen().catch(() => {});
    else document.exitFullscreen().catch(() => {});
  };

  // ── Ürünleri yükle ─────────────────────────────────────────────────────
  useEffect(() => {
    api.get("/products?page_size=5000")
      .then((d) => setProducts(d.products || d || []))
      .catch(() => toast.error("Ürünler yüklenemedi"))
      .finally(() => setLoadingProducts(false));
  }, []);

  useEffect(() => { setTimeout(() => searchRef.current?.focus(), 150); }, []);

  // ── Müşteri arama ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!showCustomerModal) return;
    setTimeout(() => custSearchRef.current?.focus(), 80);
  }, [showCustomerModal]);

  useEffect(() => {
    if (!showCustomerModal) return;
    const q = customerSearch.trim();
    setLoadingCustomers(true);
    const timer = setTimeout(() => {
      const url = q
        ? `/customers?search=${encodeURIComponent(q)}&per_page=30`
        : `/customers?per_page=30`;
      api.get(url)
        .then((d) => setCustomers(d.items || d.customers || []))
        .catch(() => setCustomers([]))
        .finally(() => setLoadingCustomers(false));
    }, 280);
    return () => clearTimeout(timer);
  }, [customerSearch, showCustomerModal]);

  // ── Klavye kısayolları ──────────────────────────────────────────────────
  useEffect(() => {
    const h = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") {
        if (e.key === "Escape") {
          e.preventDefault();
          document.activeElement.blur();
          closeAllModals();
          searchRef.current?.focus();
        }
        return;
      }
      switch (e.key) {
        case "F7": e.preventDefault(); openPriceModal(); break;
        case "F8": e.preventDefault(); requireCart(() => openPaymentModal("nakit")); break;
        case "F9": e.preventDefault(); requireCart(() => openPaymentModal("kart")); break;
        case "F10": e.preventDefault(); requireCart(() => openPaymentModal("acik_hesap")); break;
        case "F11": e.preventDefault(); clearCart(); break;
        case "Escape": e.preventDefault(); closeAllModals(); searchRef.current?.focus(); break;
        default:
          if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey)
            searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [cart]);

  const closeAllModals = () => {
    setShowPriceModal(false);
    setShowPaymentModal(false);
    setShowSplitModal(false);
    setShowReceiptModal(false);
    setShowCustomerModal(false);
  };

  const requireCart = (fn) => {
    if (cart.length === 0) { toast.error("Sepet boş — önce ürün ekleyin"); return; }
    fn();
  };

  // ── Sepet ──────────────────────────────────────────────────────────────
  const addToCart = useCallback((product) => {
    setCart((prev) => {
      const i = prev.findIndex((x) => x.product_id === product.id);
      if (i >= 0) {
        const n = [...prev];
        n[i] = { ...n[i], quantity: n[i].quantity + 1 };
        return n;
      }
      return [...prev, {
        product_id: product.id, product_name: product.name,
        sku: product.sku || "", barcode: product.barcode || product.ean || "",
        unit_price: product.price || 0, quantity: 1, discount_percent: 0,
        vat_rate: product.vat_rate ?? 18, image_url: product.image_url || "",
      }];
    });
    searchRef.current?.focus();
  }, []);

  const updateQty = (pid, qty) => {
    if (qty <= 0) setCart((p) => p.filter((i) => i.product_id !== pid));
    else setCart((p) => p.map((i) => i.product_id === pid ? { ...i, quantity: qty } : i));
  };
  const removeItem = (pid) => setCart((p) => p.filter((i) => i.product_id !== pid));
  const clearCart = () => {
    setCart([]); setCustomer(null); setGlobalDiscount(""); setPaymentAmount("");
    setSplitNakit(""); setSplitKart("");
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    const q = search.trim().toLowerCase();
    if (!q) return;
    const exact = products.find(
      (p) => p.barcode?.toLowerCase() === q || p.ean?.toLowerCase() === q || p.sku?.toLowerCase() === q
    );
    if (exact) { addToCart(exact); setSearch(""); return; }
    const matches = products.filter((p) =>
      p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q) || p.barcode?.toLowerCase().includes(q)
    );
    if (matches.length === 1) { addToCart(matches[0]); setSearch(""); }
  };

  // ── Hesaplamalar ────────────────────────────────────────────────────────
  const lineTotal = (item) => item.unit_price * item.quantity * (1 - (item.discount_percent || 0) / 100);
  const subtotal  = cart.reduce((s, i) => s + lineTotal(i), 0);
  const globalVal = parseFloat(globalDiscount) || 0;
  const globalPct = discountMode === "percent"
    ? globalVal
    : subtotal > 0 ? (globalVal / subtotal) * 100 : 0;
  const discountAmt = discountMode === "percent"
    ? subtotal * (globalVal / 100)
    : Math.min(globalVal, subtotal);
  const total     = subtotal - discountAmt;
  const vatAmt    = cart.reduce((s, i) => {
    const lt = lineTotal(i) * (1 - globalPct / 100);
    return s + lt * (i.vat_rate / (100 + i.vat_rate));
  }, 0);
  const paidNum   = parseFloat(paymentAmount) || 0;
  const change    = paidNum > total ? paidNum - total : 0;

  // Parçalı: kalan
  const splitNakitNum = parseFloat(splitNakit) || 0;
  const splitKartNum  = parseFloat(splitKart)  || 0;
  const splitRemaining = Math.max(0, total - splitNakitNum - splitKartNum);
  const splitValid     = Math.abs(splitNakitNum + splitKartNum - total) < 0.01;

  // ── Modal açıcılar ──────────────────────────────────────────────────────
  const openPriceModal = () => {
    setFoundProduct(null); setPriceQuery("");
    setShowPriceModal(true);
    setTimeout(() => priceInputRef.current?.focus(), 80);
  };

  const openPaymentModal = (type) => {
    setPaymentType(type);
    setPaymentAmount(type === "nakit" ? String(Math.ceil(total)) : total.toFixed(2));
    setShowPaymentModal(true);
    setTimeout(() => payAmountRef.current?.select(), 80);
  };

  const openSplitModal = () => {
    if (cart.length === 0) { toast.error("Sepet boş — önce ürün ekleyin"); return; }
    setSplitNakit(""); setSplitKart("");
    setShowSplitModal(true);
  };

  // ── Fiyat arama ─────────────────────────────────────────────────────────
  const handlePriceSearch = (e) => {
    e?.preventDefault();
    const q = priceQuery.trim().toLowerCase();
    if (!q) return;
    const found = products.find(
      (p) => p.barcode?.toLowerCase() === q || p.ean?.toLowerCase() === q ||
             p.sku?.toLowerCase() === q || p.name?.toLowerCase().includes(q)
    );
    setFoundProduct(found || null);
    if (!found) toast.error("Ürün bulunamadı");
  };

  // ── Satışı tamamla ──────────────────────────────────────────────────────
  const completeSale = async (opts = {}) => {
    if (!cart.length || completingSale) return;
    setCompletingSale(true);
    try {
      const payload = {
        items: cart.map((i) => ({ ...i, line_total: lineTotal(i) })),
        customer_name: customer?.name || "Genel Müşteri",
        customer_id: customer?.id || null,
        payment_type: opts.type || paymentType,
        subtotal, discount_amount: discountAmt, vat_amount: vatAmt, total,
        paid_amount: opts.paid || paidNum || total,
        change_amount: opts.change || change,
        session_number: activeSession,
        notes: opts.notes || "",
      };
      const sale = await api.post("/pos/sales", payload);
      setLastSale({ ...sale, items: cart, total, change: opts.change || change,
        payment_type: opts.type || paymentType, customer_name: payload.customer_name,
        split: opts.split || null });
      closeAllModals();
      setShowReceiptModal(true);
      clearCart();
      toast.success(`Satış tamamlandı — ${sale.sale_number}`);
    } catch (err) {
      toast.error("Satış kaydedilemedi: " + (err?.message || "Bilinmeyen hata"));
    } finally {
      setCompletingSale(false);
    }
  };

  const completeSplit = () => {
    if (!splitValid) { toast.error("Nakit + Kart toplamı fiş tutarına eşit olmalı"); return; }
    completeSale({
      type: "karma",
      paid: total,
      change: 0,
      split: { nakit: splitNakitNum, kart: splitKartNum },
      notes: `Karma ödeme: Nakit ₺${fmt(splitNakitNum)}, Kart ₺${fmt(splitKartNum)}`,
    });
  };

  // ── Kategoriler & filtre ────────────────────────────────────────────────
  const categories = ["all", ...new Set(products.map((p) => p.category).filter(Boolean))];
  const q = search.toLowerCase();
  const filtered = products.filter((p) => {
    const catOk = selectedCategory === "all" || p.category === selectedCategory;
    if (!q) return catOk;
    return catOk && (p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q) ||
      p.barcode?.toLowerCase().includes(q) || p.ean?.toLowerCase().includes(q));
  });

  // ── Saat ────────────────────────────────────────────────────────────────
  const [clock, setClock] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setClock(new Date()), 1000); return () => clearInterval(t); }, []);
  const clockStr = clock.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const dateStr  = clock.toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" });
  const totalQty = cart.reduce((s, i) => s + i.quantity, 0);

  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="flex flex-col bg-[#f1f5f9]"
      style={{ height: isFullscreen ? "100vh" : "calc(100vh - 56px)", fontFamily: "'Inter', system-ui, sans-serif" }}>

      {/* ── Üst Bar ──────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 h-12 bg-[#0c1628] border-b border-white/[0.07] flex-shrink-0">
        {/* Oturum sekmeleri */}
        <div className="flex items-center gap-1 mr-1">
          {[1,2,3,4].map((s) => {
            const has = sessions[s]?.length > 0 && s !== activeSession;
            return (
              <button key={s} onClick={() => {
                setSessions((p) => ({ ...p, [activeSession]: cart }));
                setActiveSession(s); setCart(sessions[s] || []);
              }}
                className={`relative px-3.5 py-1 rounded-md text-[11px] font-semibold transition-all ${
                  activeSession === s
                    ? "bg-[#1487a3] text-white shadow-[0_0_12px_rgba(20,135,163,0.35)]"
                    : "text-white/35 hover:text-white/65 hover:bg-white/[0.06]"}`}>
                Fiş {s}
                {has && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-amber-400 rounded-full text-[8px] text-black font-bold flex items-center justify-center">{sessions[s].length}</span>}
              </button>
            );
          })}
        </div>

        {/* Barkod arama */}
        <form onSubmit={handleSearchSubmit} className="flex-1 max-w-lg">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25 pointer-events-none" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input ref={searchRef} type="text" value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearchSubmit(e)}
              placeholder="Barkod okutun veya ürün adı / SKU yazın…"
              className="w-full pl-8 pr-4 py-1.5 rounded-lg bg-white/[0.06] border border-white/[0.1] text-white/90 placeholder-white/25 text-[13px] focus:outline-none focus:border-[#1487a3]/80 focus:bg-white/[0.09] transition-all" />
          </div>
        </form>

        <div className="ml-auto flex items-center gap-3">
          <div className="hidden lg:flex flex-col items-end">
            <span className="text-white/75 text-[13px] font-semibold tabular-nums">{clockStr}</span>
            <span className="text-white/30 text-[10px]">{dateStr}</span>
          </div>
          <button onClick={toggleFullscreen}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#1487a3]/40 bg-[#1487a3]/10 text-[#22c8e0] hover:bg-[#1487a3]/20 transition-all text-[11px] font-medium">
            {isFullscreen ? (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/></svg>
            ) : (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>
            )}
            <span className="hidden xl:inline">{isFullscreen ? "Küçült" : "Tam Ekran"}</span>
          </button>
          <button onClick={() => navigate("/panel")} className="text-white/30 hover:text-white/60 text-[11px] font-medium transition-colors">← Panele Dön</button>
        </div>
      </div>

      {/* ── Ana Gövde ─────────────────────────────────────────────────────── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* ══════════════════════════════════════════════════════════════════
            Sol Panel — Fiş (Açık tema)
        ══════════════════════════════════════════════════════════════════ */}
        <div className="flex flex-col w-[48%] min-w-0 bg-white border-r border-[#e8edf2]">

          {/* Müşteri satırı */}
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#f0f4f8] bg-[#fafbfc]">
            <button
              onClick={() => { setCustomerSearch(""); setShowCustomerModal(true); }}
              className={`flex-1 flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all text-left ${
                customer
                  ? "border-[#1487a3]/30 bg-[#1487a3]/[0.04] hover:bg-[#1487a3]/[0.07]"
                  : "border-[#e2e8f0] bg-white hover:border-[#1487a3]/40 hover:bg-[#f8fbff]"
              }`}
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${customer ? "bg-[#1487a3]" : "bg-slate-100"}`}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={customer ? "white" : "#94a3b8"} strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                {customer ? (
                  <>
                    <p className="text-[#0f172a] text-[12px] font-semibold truncate">{customer.name}</p>
                    {customer.phone && <p className="text-slate-400 text-[10px]">{customer.phone}</p>}
                  </>
                ) : (
                  <p className="text-slate-400 text-[12px]">Müşteri seç <span className="text-slate-300">(opsiyonel)</span></p>
                )}
              </div>
              <svg className="text-slate-300 flex-shrink-0" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            {customer && (
              <button onClick={() => setCustomer(null)}
                className="w-8 h-8 rounded-xl border border-[#e2e8f0] flex items-center justify-center text-slate-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-all flex-shrink-0">
                <IconX size={11} />
              </button>
            )}
          </div>

          {/* Sepet başlık */}
          <div className="flex items-center justify-between px-5 py-2.5 border-b border-[#f0f4f8]">
            <div className="flex items-center gap-2">
              <span className="text-[#0f172a] font-bold text-[12px]">Sepet</span>
              {cart.length > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-[#1487a3]/10 text-[#1487a3] text-[11px] font-bold">{totalQty} kalem</span>
              )}
            </div>
            {cart.length > 0 && (
              <button onClick={clearCart}
                className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-red-500 transition-colors">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2"/>
                </svg>
                Temizle
              </button>
            )}
          </div>

          {/* Sepet öğeleri */}
          <div className="flex-1 overflow-y-auto scrollbar-thin" style={{ scrollbarColor: "rgba(0,0,0,0.06) transparent" }}>
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 select-none">
                <div className="w-16 h-16 rounded-2xl bg-[#f8fafc] flex items-center justify-center border border-[#e8edf2]">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5">
                    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                  </svg>
                </div>
                <div className="text-center">
                  <p className="text-slate-400 text-[13px] font-medium">Sepet boş</p>
                  <p className="text-slate-300 text-[11px] mt-0.5">Barkod okutun veya sağdan ürün seçin</p>
                </div>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#f0f4f8]">
                    <th className="text-left pl-5 pr-2 py-2 text-[10px] font-bold text-slate-300 uppercase tracking-wider w-6">#</th>
                    <th className="text-left px-2 py-2 text-[10px] font-bold text-slate-300 uppercase tracking-wider">Ürün</th>
                    <th className="text-center px-2 py-2 text-[10px] font-bold text-slate-300 uppercase tracking-wider w-28">Adet</th>
                    <th className="text-right px-2 py-2 text-[10px] font-bold text-slate-300 uppercase tracking-wider w-20">B.Fiyat</th>
                    <th className="text-right pl-2 pr-5 py-2 text-[10px] font-bold text-slate-300 uppercase tracking-wider w-24">Toplam</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {cart.map((item, idx) => (
                    <tr key={item.product_id} className="group border-b border-[#f8fafc] hover:bg-[#f6f9ff] transition-colors">
                      <td className="pl-5 pr-2 py-3">
                        <span className="text-slate-300 text-[11px] tabular-nums">{idx + 1}</span>
                      </td>
                      <td className="px-2 py-3">
                        <p className="text-[#0f172a] text-[12.5px] font-semibold leading-tight truncate max-w-[160px]">{item.product_name}</p>
                        <p className="text-slate-400 text-[10px] mt-0.5">{item.sku || item.barcode || "—"}</p>
                      </td>
                      <td className="px-2 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => updateQty(item.product_id, item.quantity - 1)}
                            className="w-6 h-6 rounded-lg bg-[#f1f5f9] hover:bg-red-50 hover:text-red-500 text-slate-400 flex items-center justify-center text-sm font-bold transition-colors">−</button>
                          <input type="number" value={item.quantity}
                            onChange={(e) => updateQty(item.product_id, parseInt(e.target.value) || 0)}
                            className="w-10 text-center text-[13px] font-bold text-[#0f172a] bg-white border border-[#e2e8f0] rounded-lg py-1 focus:outline-none focus:border-[#1487a3]/60 tabular-nums"
                            min="0" />
                          <button onClick={() => updateQty(item.product_id, item.quantity + 1)}
                            className="w-6 h-6 rounded-lg bg-[#f1f5f9] hover:bg-[#e8f6f9] hover:text-[#1487a3] text-slate-400 flex items-center justify-center text-sm font-bold transition-colors">+</button>
                        </div>
                      </td>
                      <td className="px-2 py-3 text-right">
                        <p className="text-slate-400 text-[12px] tabular-nums">₺{fmt(item.unit_price)}</p>
                      </td>
                      <td className="pl-2 pr-5 py-3 text-right">
                        <p className="text-[#0f172a] text-[13px] font-bold tabular-nums">₺{fmt(lineTotal(item))}</p>
                      </td>
                      <td className="py-3 pr-2">
                        <button onClick={() => removeItem(item.product_id)}
                          className="opacity-0 group-hover:opacity-100 w-6 h-6 rounded-lg flex items-center justify-center text-slate-300 hover:text-red-400 hover:bg-red-50 transition-all">
                          <IconX size={10} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Özet + İndirim */}
          <div className="border-t border-[#e8edf2] bg-[#fafbfc] px-4 py-3 space-y-2">
            {/* İndirim satırı */}
            <div className="flex items-center gap-2 p-2.5 rounded-xl bg-white border border-[#e2e8f0]">
              <svg className="text-amber-400 flex-shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>
              </svg>
              <span className="text-[12px] font-semibold text-slate-600 flex-1">İndirim</span>
              {/* % / ₺ toggle */}
              <div className="flex rounded-lg border border-[#e2e8f0] overflow-hidden">
                <button onClick={() => { setDiscountMode("percent"); setGlobalDiscount(""); }}
                  className={`px-2.5 py-1 text-[11px] font-bold transition-colors ${discountMode === "percent" ? "bg-amber-500 text-white" : "bg-white text-slate-400 hover:text-slate-600"}`}>
                  %
                </button>
                <button onClick={() => { setDiscountMode("amount"); setGlobalDiscount(""); }}
                  className={`px-2.5 py-1 text-[11px] font-bold transition-colors ${discountMode === "amount" ? "bg-amber-500 text-white" : "bg-white text-slate-400 hover:text-slate-600"}`}>
                  ₺
                </button>
              </div>
              <input
                type="number"
                value={globalDiscount}
                onChange={(e) => setGlobalDiscount(e.target.value)}
                placeholder={discountMode === "percent" ? "0" : "0,00"}
                min="0"
                max={discountMode === "percent" ? "100" : undefined}
                className="w-20 text-right text-[13px] font-bold text-[#0f172a] bg-[#f8fafc] border border-[#e2e8f0] rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-amber-400 tabular-nums"
              />
            </div>

            <div className="flex justify-between text-[12px]">
              <span className="text-slate-400">Ara Toplam</span>
              <span className="text-slate-600 tabular-nums font-medium">₺{fmt(subtotal)}</span>
            </div>
            {discountAmt > 0 && (
              <div className="flex justify-between text-[12px]">
                <span className="text-amber-600 font-medium">
                  İndirim {discountMode === "percent" ? `(%${globalVal})` : ""}
                </span>
                <span className="text-amber-600 tabular-nums font-bold">−₺{fmt(discountAmt)}</span>
              </div>
            )}
            <div className="flex justify-between text-[11px]">
              <span className="text-slate-300">KDV (dahil)</span>
              <span className="text-slate-400 tabular-nums">₺{fmt(vatAmt)}</span>
            </div>
          </div>

          {/* Toplam bar */}
          <div className="flex-shrink-0 border-t-2 border-[#1487a3]/20 bg-white px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-[10px] uppercase tracking-[0.14em] font-semibold mb-1">Ödenecek Tutar</p>
                <p className="tabular-nums font-black leading-none text-[#0f172a]"
                  style={{ fontFamily: "'Manrope', sans-serif", fontSize: total > 9999 ? "30px" : "36px", letterSpacing: "-0.025em" }}>
                  ₺{fmt(total)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            Sağ Panel — Ürünler
        ══════════════════════════════════════════════════════════════════ */}
        <div className="flex flex-col flex-1 min-w-0 bg-[#f8fafc]">

          {/* Kategori chips */}
          <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-[#e2e8f0] bg-white overflow-x-auto flex-shrink-0" style={{ scrollbarWidth: "none" }}>
            {categories.map((cat) => {
              const cnt = cat === "all" ? products.length : products.filter((p) => p.category === cat).length;
              return (
                <button key={cat} onClick={() => setSelectedCategory(cat)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold whitespace-nowrap transition-all ${
                    selectedCategory === cat
                      ? "bg-[#0c1628] text-white shadow-sm"
                      : "bg-[#f1f5f9] text-slate-500 hover:bg-[#e8edf4] hover:text-slate-700"}`}>
                  {cat === "all" ? "Tümü" : cat}
                  <span className={`text-[10px] font-bold ${selectedCategory === cat ? "text-white/60" : "text-slate-400"}`}>{cnt}</span>
                </button>
              );
            })}
          </div>

          {/* Ürün ızgarası */}
          <div className="flex-1 overflow-y-auto p-3 scrollbar-thin" style={{ scrollbarColor: "rgba(0,0,0,0.08) transparent" }}>
            {loadingProducts ? (
              <div className="grid grid-cols-3 gap-2.5">{Array.from({length:9}).map((_,i) => <div key={i} className="rounded-2xl bg-white border border-[#e8edf2] h-36 animate-pulse" />)}</div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-2">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <p className="text-[13px]">Ürün bulunamadı</p>
                {search && <p className="text-[11px]">"{search}" için eşleşme yok</p>}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2.5">
                {filtered.slice(0, 120).map((product) => {
                  const inCart = cart.find((i) => i.product_id === product.id);
                  const out = product.stock != null && product.stock <= 0;
                  return (
                    <button key={product.id} onClick={() => !out && addToCart(product)} disabled={out}
                      className={`relative flex flex-col items-start p-3 rounded-2xl bg-white text-left transition-all duration-150 ${
                        out ? "opacity-45 cursor-not-allowed border border-[#e8edf2]"
                        : inCart ? "border-2 border-[#1487a3] shadow-[0_0_0_4px_rgba(20,135,163,0.08)] hover:shadow-[0_4px_20px_rgba(20,135,163,0.15)]"
                        : "border border-[#e8edf2] hover:border-[#1487a3]/40 hover:shadow-[0_4px_16px_rgba(0,0,0,0.07)] hover:-translate-y-0.5"}`}>
                      {inCart && (
                        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#1487a3] text-white text-[11px] font-bold flex items-center justify-center shadow-sm">{inCart.quantity}</div>
                      )}
                      <ProductAvatar name={product.name} imageUrl={product.image_url} size={44} />
                      <p className="mt-2.5 text-[11.5px] font-medium text-[#1e293b] leading-snug line-clamp-2 min-h-[32px]">{product.name}</p>
                      <div className="mt-auto pt-2 w-full flex items-end justify-between">
                        <div>
                          <p className="text-[15px] font-black text-[#0f172a] tabular-nums" style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.02em" }}>₺{fmt(product.price)}</p>
                          <StockPill stock={product.stock} />
                        </div>
                        {!out && (
                          <div className={`w-7 h-7 rounded-xl flex items-center justify-center transition-all ${inCart ? "bg-[#1487a3] text-white" : "bg-[#f1f5f9] text-slate-400"}`}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Ödeme Butonları ─────────────────────────────────────────── */}
          <div className="border-t border-[#e2e8f0] bg-white px-3 py-3 grid grid-cols-4 gap-2 flex-shrink-0">
            {/* Nakit */}
            <button onClick={() => requireCart(() => openPaymentModal("nakit"))}
              className="flex flex-col items-center justify-center gap-1.5 py-3.5 rounded-2xl bg-gradient-to-b from-emerald-500 to-emerald-600 text-white font-bold text-[13px] shadow-[0_4px_12px_rgba(16,185,129,0.3)] hover:shadow-[0_6px_20px_rgba(16,185,129,0.4)] hover:-translate-y-0.5 transition-all">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
              Nakit
              <span className="text-[10px] font-medium opacity-70">F8</span>
            </button>
            {/* Kart */}
            <button onClick={() => requireCart(() => openPaymentModal("kart"))}
              className="flex flex-col items-center justify-center gap-1.5 py-3.5 rounded-2xl bg-gradient-to-b from-[#1487a3] to-[#0e6a83] text-white font-bold text-[13px] shadow-[0_4px_12px_rgba(20,135,163,0.3)] hover:shadow-[0_6px_20px_rgba(20,135,163,0.4)] hover:-translate-y-0.5 transition-all">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/><line x1="5" y1="15" x2="9" y2="15"/></svg>
              Kart
              <span className="text-[10px] font-medium opacity-70">F9</span>
            </button>
            {/* Açık Hesap */}
            <button onClick={() => requireCart(() => openPaymentModal("acik_hesap"))}
              className="flex flex-col items-center justify-center gap-1.5 py-3.5 rounded-2xl bg-gradient-to-b from-amber-500 to-amber-600 text-white font-bold text-[13px] shadow-[0_4px_12px_rgba(245,158,11,0.25)] hover:shadow-[0_6px_20px_rgba(245,158,11,0.35)] hover:-translate-y-0.5 transition-all">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              Açık Hesap
              <span className="text-[10px] font-medium opacity-70">F10</span>
            </button>
            {/* Parçalı */}
            <button onClick={openSplitModal}
              className="flex flex-col items-center justify-center gap-1.5 py-3.5 rounded-2xl bg-gradient-to-b from-violet-500 to-violet-600 text-white font-bold text-[13px] shadow-[0_4px_12px_rgba(139,92,246,0.25)] hover:shadow-[0_6px_20px_rgba(139,92,246,0.35)] hover:-translate-y-0.5 transition-all">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
              Parçalı
              <span className="text-[10px] font-medium opacity-70">Karma</span>
            </button>
          </div>

          {/* Fiyatını Gör butonu — ayrı satır, daha kompakt */}
          <div className="border-t border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 flex justify-end">
            <button onClick={openPriceModal}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-white border border-[#e2e8f0] text-slate-500 hover:text-slate-700 hover:border-slate-300 font-medium text-[12px] transition-all">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              Fiyatını Gör
              <span className="text-slate-400 text-[10px]">F7</span>
            </button>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          Modal: Müşteri Seç
      ════════════════════════════════════════════════════════════════════ */}
      {showCustomerModal && (
        <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col max-h-[70vh]">
            <div className="px-5 py-4 border-b border-[#e8edf2] flex items-center gap-3">
              <div className="flex-1">
                <h2 className="font-bold text-[15px] text-[#0f172a]" style={{ fontFamily: "'Manrope',sans-serif" }}>Müşteri Seç</h2>
              </div>
              <button onClick={() => setShowCustomerModal(false)}
                className="w-8 h-8 rounded-xl bg-[#f1f5f9] hover:bg-[#e2e8f0] flex items-center justify-center text-slate-400 transition-colors">
                <IconX />
              </button>
            </div>
            <div className="px-4 py-3 border-b border-[#f0f4f8]">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
                <input ref={custSearchRef} type="text" value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder="İsim, telefon veya e-posta ile ara…"
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] text-[13px] focus:outline-none focus:border-[#1487a3]/60" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto scrollbar-thin">
              {/* Genel müşteri seçeneği */}
              <button
                onClick={() => { setCustomer({ name: "Genel Müşteri" }); setShowCustomerModal(false); }}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-[#f8fbff] border-b border-[#f0f4f8] text-left transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-[#0f172a]">Genel Müşteri</p>
                  <p className="text-[11px] text-slate-400">Müşteri kaydı olmadan satış yap</p>
                </div>
              </button>

              {loadingCustomers ? (
                <div className="py-8 text-center text-slate-400 text-[13px]">Yükleniyor…</div>
              ) : customers.length === 0 ? (
                <div className="py-8 text-center text-slate-400 text-[13px]">
                  {customerSearch ? "Müşteri bulunamadı" : "Müşteri arayın"}
                </div>
              ) : (
                customers.map((c) => (
                  <button key={c.id || c._id || c.email}
                    onClick={() => { setCustomer({ name: c.name || c.full_name, email: c.email, phone: c.phone, id: c.id || c._id }); setShowCustomerModal(false); }}
                    className="w-full flex items-center gap-3 px-5 py-3 hover:bg-[#f8fbff] border-b border-[#f0f4f8] text-left transition-colors"
                  >
                    <div className="w-9 h-9 rounded-full bg-[#1487a3]/10 flex items-center justify-center flex-shrink-0 text-[#1487a3] font-bold text-[13px]">
                      {(c.name || c.full_name || "?")[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-[#0f172a] truncate">{c.name || c.full_name}</p>
                      <p className="text-[11px] text-slate-400 truncate">{c.phone || c.email || ""}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          Modal: Tekli Ödeme (Nakit / Kart / Açık Hesap)
      ════════════════════════════════════════════════════════════════════ */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/55 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className={`px-6 py-5 flex items-center justify-between ${
              paymentType === "nakit" ? "bg-gradient-to-r from-emerald-700 to-emerald-500"
              : paymentType === "kart" ? "bg-gradient-to-r from-[#0c1628] to-[#1487a3]"
              : "bg-gradient-to-r from-amber-700 to-amber-500"}`}>
              <div>
                <h2 className="text-white font-bold text-[17px]" style={{ fontFamily: "'Manrope',sans-serif" }}>
                  {paymentType === "nakit" ? "Nakit Ödeme" : paymentType === "kart" ? "Kart ile Ödeme" : "Açık Hesap"}
                </h2>
                <p className="text-white/60 text-[11px] mt-0.5">{totalQty} kalem · {customer?.name || "Genel Müşteri"}</p>
              </div>
              <button onClick={() => setShowPaymentModal(false)}
                className="w-8 h-8 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors">
                <IconX />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-[#e8edf2]">
                <span className="text-slate-400 text-[13px]">Tahsil Edilecek</span>
                <span className="text-[28px] font-black text-[#0f172a] tabular-nums" style={{ fontFamily: "'Manrope',sans-serif", letterSpacing: "-0.02em" }}>₺{fmt(total)}</span>
              </div>

              {paymentType === "nakit" && (
                <>
                  <div>
                    <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2 block">Alınan Tutar</label>
                    <input ref={payAmountRef} type="number" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] text-[22px] font-bold text-right text-[#0f172a] focus:outline-none focus:border-[#1487a3]/60 tabular-nums" />
                  </div>
                  <div className="grid grid-cols-4 gap-1.5">
                    {[50, 100, 200, 500].map((a) => (
                      <button key={a} onClick={() => setPaymentAmount(String(a))}
                        className={`py-2 rounded-xl text-[12px] font-bold border transition-all ${
                          parseFloat(paymentAmount) === a
                            ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                            : "border-[#e8edf2] text-slate-500 hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-700"}`}>
                        ₺{a}
                      </button>
                    ))}
                  </div>
                  {change > 0 && (
                    <div className="flex justify-between items-center p-3 rounded-2xl bg-emerald-50 border border-emerald-200">
                      <span className="text-emerald-700 text-[13px] font-semibold">Para Üstü</span>
                      <span className="text-emerald-700 text-[22px] font-black tabular-nums" style={{ fontFamily: "'Manrope',sans-serif" }}>₺{fmt(change)}</span>
                    </div>
                  )}
                </>
              )}

              {paymentType === "acik_hesap" && !customer && (
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-[12px] flex gap-2 items-start">
                  <svg className="mt-0.5 flex-shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  Açık hesap için müşteri seçilmesi önerilir.
                </div>
              )}

              <button onClick={() => completeSale()}
                disabled={completingSale || (paymentType === "nakit" && paidNum < total - 0.01)}
                className={`w-full py-4 rounded-2xl font-bold text-white text-[15px] transition-all shadow-lg ${
                  paymentType === "nakit" ? "bg-gradient-to-b from-emerald-500 to-emerald-600 disabled:opacity-40"
                  : paymentType === "kart" ? "bg-gradient-to-b from-[#1487a3] to-[#0d6e87]"
                  : "bg-gradient-to-b from-amber-500 to-amber-600"
                } disabled:cursor-not-allowed`}
                style={{ fontFamily: "'Manrope',sans-serif" }}>
                {completingSale ? "İşleniyor…" : "Satışı Tamamla →"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          Modal: Parçalı / Karma Ödeme
      ════════════════════════════════════════════════════════════════════ */}
      {showSplitModal && (
        <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/55 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="px-6 py-5 bg-gradient-to-r from-violet-700 to-violet-500 flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-[17px]" style={{ fontFamily: "'Manrope',sans-serif" }}>Parçalı Ödeme</h2>
                <p className="text-white/60 text-[11px] mt-0.5">{totalQty} kalem · {customer?.name || "Genel Müşteri"}</p>
              </div>
              <button onClick={() => setShowSplitModal(false)}
                className="w-8 h-8 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors">
                <IconX />
              </button>
            </div>

            <div className="px-6 py-2 bg-violet-50 border-b border-violet-100">
              <p className="text-violet-700 text-[12px] py-2">
                Fiş tutarını ödeme tiplerine bölün. Nakit + Kart toplamı fiş tutarına eşit olmalıdır.
              </p>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Toplam / Kalan */}
              <div className="flex gap-4">
                <div className="flex-1 p-3 rounded-xl bg-[#f8fafc] border border-[#e8edf2]">
                  <p className="text-slate-400 text-[10px] uppercase tracking-wider font-semibold mb-1">TOPLAM</p>
                  <p className="text-[#0f172a] text-[20px] font-black tabular-nums" style={{ fontFamily: "'Manrope',sans-serif" }}>₺{fmt(total)}</p>
                </div>
                <div className={`flex-1 p-3 rounded-xl border ${splitRemaining < 0.01 ? "bg-emerald-50 border-emerald-200" : "bg-[#f8fafc] border-[#e8edf2]"}`}>
                  <p className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${splitRemaining < 0.01 ? "text-emerald-600" : "text-slate-400"}`}>KALAN</p>
                  <p className={`text-[20px] font-black tabular-nums ${splitRemaining < 0.01 ? "text-emerald-600" : "text-[#0f172a]"}`} style={{ fontFamily: "'Manrope',sans-serif" }}>
                    ₺{fmt(splitRemaining)}
                  </p>
                </div>
              </div>

              {/* Nakit satırı */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[12px] font-semibold text-slate-600 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                    </span>
                    Nakit
                  </label>
                  <button
                    onClick={() => setSplitNakit(splitRemaining > 0 ? (splitNakitNum + splitRemaining).toFixed(2) : total.toFixed(2))}
                    className="text-[11px] text-[#1487a3] font-semibold hover:underline">
                    Kalanı Yaz
                  </button>
                </div>
                <input type="number" value={splitNakit}
                  onChange={(e) => setSplitNakit(e.target.value)}
                  placeholder="0,00"
                  className="w-full px-4 py-3 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] text-[18px] font-bold text-right text-[#0f172a] focus:outline-none focus:border-emerald-400 tabular-nums" />
              </div>

              {/* Kart satırı */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[12px] font-semibold text-slate-600 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-lg bg-[#1487a3]/10 text-[#1487a3] flex items-center justify-center">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                    </span>
                    Kart / POS
                  </label>
                  <button
                    onClick={() => setSplitKart(splitRemaining > 0 ? (splitKartNum + splitRemaining).toFixed(2) : total.toFixed(2))}
                    className="text-[11px] text-[#1487a3] font-semibold hover:underline">
                    Kalanı Yaz
                  </button>
                </div>
                <input type="number" value={splitKart}
                  onChange={(e) => setSplitKart(e.target.value)}
                  placeholder="0,00"
                  className="w-full px-4 py-3 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] text-[18px] font-bold text-right text-[#0f172a] focus:outline-none focus:border-[#1487a3]/60 tabular-nums" />
              </div>

              {/* Butonlar */}
              <div className="flex gap-2 pt-1">
                <button onClick={() => setShowSplitModal(false)}
                  className="px-5 py-3 rounded-xl border border-[#e2e8f0] text-slate-600 text-[13px] font-semibold hover:bg-[#f1f5f9] transition-colors">
                  Vazgeç
                </button>
                <button onClick={completeSplit} disabled={!splitValid || completingSale}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-b from-violet-500 to-violet-600 text-white font-bold text-[14px] transition-all shadow-lg disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-violet-200"
                  style={{ fontFamily: "'Manrope',sans-serif" }}>
                  {completingSale ? "İşleniyor…" : splitValid ? "Satışı Tamamla →" : `Kalan: ₺${fmt(splitRemaining)}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          Modal: Fiyatını Gör (F7)
      ════════════════════════════════════════════════════════════════════ */}
      {showPriceModal && (
        <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/55 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-[#0c1628] to-[#1487a3] flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-[16px]" style={{ fontFamily: "'Manrope',sans-serif" }}>Fiyatını Gör</h2>
                <p className="text-white/50 text-[11px] mt-0.5">Barkod, SKU veya ürün adı girin</p>
              </div>
              <button onClick={() => setShowPriceModal(false)}
                className="w-8 h-8 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 transition-colors">
                <IconX />
              </button>
            </div>
            <div className="px-5 py-5">
              <form onSubmit={handlePriceSearch} className="flex gap-2">
                <input ref={priceInputRef} type="text" value={priceQuery}
                  onChange={(e) => { setPriceQuery(e.target.value); setFoundProduct(null); }}
                  placeholder="Barkod okutun veya yazın…"
                  className="flex-1 px-3 py-2.5 rounded-xl border border-[#e2e8f0] text-[13px] bg-[#f8fafc] focus:outline-none focus:border-[#1487a3]/60"
                  autoComplete="off" />
                <button type="submit" className="px-4 py-2.5 rounded-xl bg-[#0c1628] text-white text-[13px] font-medium hover:bg-[#1487a3] transition-colors">Ara</button>
              </form>
              {foundProduct && (
                <div className="mt-4 p-4 rounded-2xl border border-[#e8edf2] bg-[#f8fafc]">
                  <div className="flex items-start gap-3">
                    <ProductAvatar name={foundProduct.name} imageUrl={foundProduct.image_url} size={56} />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#0f172a] text-[13px] leading-snug">{foundProduct.name}</p>
                      <p className="text-slate-400 text-[11px] mt-0.5">{foundProduct.sku}</p>
                      <div className="mt-2 flex items-baseline gap-2">
                        <span className="text-[26px] font-black text-[#0f172a] tabular-nums" style={{ fontFamily: "'Manrope',sans-serif", letterSpacing: "-0.02em" }}>₺{fmt(foundProduct.price)}</span>
                        <StockPill stock={foundProduct.stock} />
                      </div>
                      <p className="text-slate-400 text-[10px] mt-0.5">KDV %{foundProduct.vat_rate ?? 18} dahil</p>
                    </div>
                  </div>
                  <button onClick={() => { addToCart(foundProduct); setShowPriceModal(false); }}
                    className="mt-4 w-full py-2.5 rounded-xl bg-[#1487a3] text-white font-semibold text-[13px] hover:bg-[#0d6e87] transition-colors">
                    Sepete Ekle
                  </button>
                </div>
              )}
              {priceQuery && !foundProduct && (
                <div className="mt-4 py-8 text-center text-slate-400 text-[13px]">Ürün bulunamadı — barkod ya da SKU'yu kontrol edin</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          Modal: Fiş / Makbuz
      ════════════════════════════════════════════════════════════════════ */}
      {showReceiptModal && lastSale && (
        <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/55 backdrop-blur-sm p-4">
          <div className="w-full max-w-xs rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="px-6 pt-6 pb-4 text-center">
              <div className="w-14 h-14 rounded-full bg-gradient-to-b from-emerald-400 to-emerald-600 shadow-[0_8px_24px_rgba(16,185,129,0.35)] flex items-center justify-center mx-auto mb-3">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <h2 className="font-black text-[18px] text-[#0f172a]" style={{ fontFamily: "'Manrope',sans-serif" }}>Satış Tamamlandı</h2>
              <p className="text-slate-400 text-[12px] mt-1 font-mono">{lastSale.sale_number}</p>
            </div>
            <div className="px-6 py-3 bg-[#f8fafc] border-t border-b border-[#e8edf2] space-y-2">
              <div className="flex justify-between text-[12px]">
                <span className="text-slate-400">Müşteri</span>
                <span className="font-semibold text-[#0f172a]">{lastSale.customer_name}</span>
              </div>
              <div className="flex justify-between text-[12px]">
                <span className="text-slate-400">Ödeme</span>
                <span className="font-semibold text-[#0f172a]">
                  {lastSale.payment_type === "nakit" ? "Nakit"
                    : lastSale.payment_type === "kart" ? "Kredi Kartı"
                    : lastSale.payment_type === "karma" ? "Karma"
                    : "Açık Hesap"}
                </span>
              </div>
              {lastSale.split && (
                <div className="pl-4 space-y-1">
                  <div className="flex justify-between text-[11px]">
                    <span className="text-slate-400">↳ Nakit</span>
                    <span className="text-slate-600 tabular-nums">₺{fmt(lastSale.split.nakit)}</span>
                  </div>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-slate-400">↳ Kart</span>
                    <span className="text-slate-600 tabular-nums">₺{fmt(lastSale.split.kart)}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4">
              <div className="flex justify-between items-center mb-4">
                <span className="text-slate-500 text-[13px]">Toplam</span>
                <span className="text-[28px] font-black text-[#1487a3] tabular-nums" style={{ fontFamily: "'Manrope',sans-serif", letterSpacing: "-0.02em" }}>₺{fmt(lastSale.total)}</span>
              </div>
              {lastSale.change > 0 && (
                <div className="flex justify-between items-center mb-3 text-emerald-600 text-[13px]">
                  <span>Para Üstü</span>
                  <span className="font-bold">₺{fmt(lastSale.change)}</span>
                </div>
              )}
              <button onClick={() => { setShowReceiptModal(false); setTimeout(() => searchRef.current?.focus(), 50); }}
                className="w-full py-3 rounded-2xl bg-[#0c1628] hover:bg-[#1487a3] text-white font-bold text-[14px] transition-colors"
                style={{ fontFamily: "'Manrope',sans-serif" }}>
                Yeni Satış
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
